import {isProdEnv, timeNow} from "@/utils/helpers";

const RULES_ACCESS_DEBUG_PREFIX = "[RulesAccessDebug]"
const RULES_ACCESS_REPORT_NAME = "RulesAccessDenied"
const RULES_ACCESS_PENDING_CONTEXT_LIMIT = 100

function pickDefinedEntries(object = {}) {
	let result = {}
	Object.keys(object).forEach(key => {
		if(object[key] !== undefined){
			result[key] = object[key]
		}
	})
	return result
}

function normalizeRulesAccessPath(path = "") {
	return path || null
}

function isRulesAccessPermissionDenied(error){
	return error?.code === "permission-denied"
}

export default class RulesAccessDebugHelper {

	dataManager
	pendingContexts = []
	isReportInFlight = false

	constructor(dataManager){
		this.dataManager = dataManager
	}

	pushContext(methodName, parsedPath, data, clazz, options){
		let context = {
			methodName,
			parsedPath: normalizeRulesAccessPath(parsedPath),
			operation: this.getOperation(methodName),
			targetIds: this.extractTargetIds(data),
			className: clazz?.name || null,
			optionsSummary: this.summarizeOptions(options),
			createdAt: timeNow(),
		}
		this.pendingContexts.push(context)
		if(this.pendingContexts.length > RULES_ACCESS_PENDING_CONTEXT_LIMIT){
			this.pendingContexts.splice(0, this.pendingContexts.length - RULES_ACCESS_PENDING_CONTEXT_LIMIT)
		}
	}

	getOperation(methodName = ""){
		let operation = "unknown"
		if(methodName.startsWith("get") && (methodName.endsWith("List") || methodName.endsWith("ListPager") || methodName.endsWith("Group") || methodName.endsWith("GroupPager"))){
			operation = "list"
		} else if(methodName.startsWith("get") && (methodName.endsWith("Count") || methodName.endsWith("Average") || methodName.endsWith("Sum") || methodName.startsWith("getAggregate"))){
			operation = "aggregate"
		} else if(methodName.startsWith("get")){
			operation = "get"
		} else if(methodName.startsWith("create")){
			operation = "create"
		} else if(methodName.startsWith("update")){
			operation = "update"
		} else if(methodName.startsWith("set")){
			operation = "set"
		} else if(methodName.startsWith("delete")){
			operation = "delete"
		} else if(methodName.startsWith("onSnapshot")){
			operation = "onSnapshot"
		}
		return operation
	}

	consumeContext(path, operation){
		path = normalizeRulesAccessPath(path)
		let matchIndex = this.pendingContexts.findIndex(item => item.parsedPath === path && item.operation === operation)
		if(matchIndex < 0){
			matchIndex = this.pendingContexts.findIndex(item => item.parsedPath === path)
		}
		let context = null
		if(matchIndex >= 0){
			context = this.pendingContexts.splice(matchIndex, 1)[0]
		}
		return context
	}

	getLocation(){
		let locationInfo = null
		if(typeof window !== "undefined" && window?.location){
			locationInfo = {
				href: window.location.href || null,
				pathname: window.location.pathname || null,
				hash: window.location.hash || null,
			}
		}
		return locationInfo
	}

	summarizeOptions(options = {}){
		return pickDefinedEntries({
			methodName: options?.methodName,
			disableLogs: options?.disableLogs === true ? true : undefined,
			triggerMeta: options?.triggerMeta ? true : undefined,
			isPortalDecorated: options?.isPortalDecorated === true ? true : undefined,
		})
	}

	extractTargetIds(data){
		let targetIds = {}
		if(Array.isArray(data)){
			targetIds.count = data.length
			targetIds.ids = data.map(item => item?.id).filter(Boolean).slice(0, 10)
		} else if(data && typeof data === "object"){
			Object.keys(data).forEach(key => {
				let value = data[key]
				let isTargetKey = key === "id" || key.endsWith("Id") || key.endsWith("Ids")
				if(isTargetKey && value != null){
					targetIds[key] = value
				}
			})
		}
		return targetIds
	}

	buildSnapshot(extra = {}){
		let currentUser = this.dataManager.getCurrentUser() || {}
		let userProfile = this.dataManager.getCurrentUserProfileStore()
		let ownerId = extra.ownerId || this.dataManager.ownerId || userProfile?.getOwnerId?.() || userProfile?.currentOwnerId || null
		let accessRuleItem = userProfile?.getCurrentOwnerAccessRuleItem?.(ownerId)
		let portalRef = userProfile?.currentPortalRef || null
		return pickDefinedEntries({
			uid: currentUser.uid || null,
			firebaseUid: this.dataManager.auth?.currentUser?.uid || null,
			userProfileId: userProfile?.id || this.dataManager.currentUserId || null,
			ownerId,
			role: userProfile?.role || null,
			roles: accessRuleItem?.roles || null,
			customPermissions: accessRuleItem?.customPermissions || null,
			clearanceLevel: accessRuleItem?.clearanceLevel || null,
			myIdForOwner: accessRuleItem?.myIdForOwner || null,
			portalRole: portalRef?.role || null,
			portalOwnerId: portalRef?.ownerId || null,
			route: this.getLocation(),
			...extra,
		})
	}

	logSnapshot(reason, extra = {}){
		let payload = this.buildSnapshot({
			reason,
			...extra,
		})
		console.log(RULES_ACCESS_DEBUG_PREFIX, JSON.stringify(payload))
		return payload
	}

	buildDeniedPayload(context = {}, error){
		let snapshot = this.buildSnapshot(context.snapshotExtra || {})
		return pickDefinedEntries({
			type: RULES_ACCESS_REPORT_NAME,
			operation: context.operation || null,
			methodName: context.methodName || null,
			path: normalizeRulesAccessPath(context.path),
			targetIds: context.targetIds || null,
			className: context.className || null,
			options: context.optionsSummary || null,
			errorCode: error?.code || null,
			errorMessage: error?.message || null,
			timestamp: timeNow(),
			...snapshot,
		})
	}

	logDenied(context, error){
		if(!isRulesAccessPermissionDenied(error)){
			return
		}
		console.error('[AuthTrace]', JSON.stringify({event: 'database.permission_denied', at: new Date().toISOString(),
			operation: context.operation, path: context.path, code: error.code,
			firebaseUid: this.dataManager.auth?.currentUser?.uid || null,
			dataUserId: this.dataManager.currentUserId || null, dataOwnerId: this.dataManager.ownerId || null}))
		let payload = this.buildDeniedPayload(context, error)
		console.warn(RULES_ACCESS_DEBUG_PREFIX, JSON.stringify(payload))
		void this.reportDenied(payload)
	}

	async reportDenied(payload){
		if(!isProdEnv || this.isReportInFlight){
			return
		}
		this.isReportInFlight = true
		try{
			await this.dataManager.apiPostLogError({
				name: RULES_ACCESS_REPORT_NAME,
				message: `Permission denied for ${payload.methodName || payload.operation || "unknown"}`,
				info: payload,
				userId: payload.uid || null,
			})
		}catch (error){
			console.warn(RULES_ACCESS_DEBUG_PREFIX, JSON.stringify({
				type: "RulesAccessReportFailed",
				errorCode: error?.code || null,
				errorMessage: error?.message || null,
				originalPath: payload?.path || null,
				originalMethodName: payload?.methodName || null,
			}))
		}
		this.isReportInFlight = false
	}

	createContext(ref, operation, data = null, clazz = null, options = {}){
		let matchedContext = this.consumeContext(ref?.path, operation) || {}
		return {
			operation,
			methodName: matchedContext.methodName || options?.methodName || null,
			path: normalizeRulesAccessPath(ref?.path || matchedContext.parsedPath),
			targetIds: Object.keys(matchedContext.targetIds || {}).length > 0 ? matchedContext.targetIds : this.extractTargetIds(data),
			className: matchedContext.className || clazz?.name || null,
			optionsSummary: Object.keys(matchedContext.optionsSummary || {}).length > 0 ? matchedContext.optionsSummary : this.summarizeOptions(options),
		}
	}

	async executeWithLogging(context, callback){
		let result = null
		try{
			result = await callback()
		}catch (error){
			this.logDenied(context, error)
			throw error
		}
		return result
	}

	wrapPager(pager, context){
		let originalNext = pager.next.bind(pager)
		pager.next = async () => {
			return await this.executeWithLogging(context, async () => {
				return await originalNext()
			})
		}
		return pager
	}

	async onSnapshot(ref, clazz, callback, options = {}){
		let context = this.createContext(ref, "onSnapshot", null, clazz, options)
		return await this.dataManager.superOnSnapshot(ref, clazz, (data, error) => {
			if(error){
				this.logDenied(context, error)
			}
			callback(data, error)
		}, options)
	}
}
